import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Typography,
  message,
  Space,
  Select,
  Alert,
  Popconfirm
} from 'antd';
import { ReloadOutlined, UserAddOutlined } from '@ant-design/icons';
import devToolsService, { ProductCustomerPair } from '../services/devToolsService.ts';
import { customerAPI } from '../services/api.ts';

const { Title, Text } = Typography;
const { Option } = Select;

interface StockItem {
  product_id: number;
  internal_product_code: string | null;
  client_product_code: string | null;
  product_description: string | null;
  stock_rows: number;
  total_quantity: number;
}

interface CustomerOption {
  id: number;
  company: string;
}

const StockWithoutCustomer: React.FC = () => {
  const [items, setItems] = useState<StockItem[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [assignments, setAssignments] = useState<Record<number, number | undefined>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [data, customersData] = await Promise.all([
        devToolsService.getStockWithoutCustomer(),
        customerAPI.getCustomers()
      ]);
      setItems(data?.items || []);
      setCustomers(customersData || []);
      setAssignments({});
    } catch (error) {
      console.error('Errore caricamento prodotti senza cliente:', error);
      message.error('Errore nel caricamento dei prodotti senza cliente');
    } finally {
      setLoading(false);
    }
  };

  const setAssignment = (productId: number, customerId: number | undefined) => {
    setAssignments((prev) => ({ ...prev, [productId]: customerId }));
  };

  const pairs: ProductCustomerPair[] = useMemo(
    () => Object.entries(assignments)
      .filter(([, customerId]) => !!customerId)
      .map(([productId, customerId]) => ({ product_id: Number(productId), customer_id: customerId as number })),
    [assignments]
  );

  const handleAssociate = async () => {
    if (pairs.length === 0) {
      message.warning('Assegna un cliente ad almeno un prodotto');
      return;
    }
    setSaving(true);
    try {
      const res = await devToolsService.associateStockWithoutCustomer(pairs);
      message.success(`Associazioni create: ${res.created}, già esistenti: ${res.skipped}`);
      if (res.errors && res.errors.length > 0) {
        message.warning(`${res.errors.length} righe non elaborate (prodotto/cliente non valido)`);
      }
      await loadData();
    } catch (error: any) {
      console.error('Errore associazione prodotti:', error);
      message.error(error.response?.data?.detail || "Errore nell'associazione dei prodotti");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Codice Interno', dataIndex: 'internal_product_code', key: 'internal_product_code',
      render: (v: string) => <Text code>{v || '-'}</Text> },
    { title: 'Codice Cliente', dataIndex: 'client_product_code', key: 'client_product_code',
      render: (v: string) => v || '-' },
    { title: 'Descrizione', dataIndex: 'product_description', key: 'product_description',
      render: (v: string) => v || '-' },
    { title: 'Righe Stock', dataIndex: 'stock_rows', key: 'stock_rows', align: 'right' as const, width: 110 },
    { title: 'Q.tà Totale', dataIndex: 'total_quantity', key: 'total_quantity', align: 'right' as const, width: 120 },
    {
      title: 'Cliente da associare',
      key: 'assign',
      width: 320,
      render: (_: any, record: StockItem) => (
        <Select
          placeholder="Nessuno (non associare)"
          showSearch
          allowClear
          optionFilterProp="children"
          style={{ width: '100%' }}
          value={assignments[record.product_id]}
          onChange={(value) => setAssignment(record.product_id, value)}
        >
          {customers.map((c) => (
            <Option key={c.id} value={c.id}>{c.company}</Option>
          ))}
        </Select>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={2}><UserAddOutlined /> Prodotti in Stock Senza Cliente</Title>

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Prodotti presenti nello stock senza cliente (product_stocks.customer_id assente) e privi di qualsiasi associazione in product_customers. Per ogni prodotto scegli il cliente da associare (oppure lascia vuoto per saltarlo)."
          description="Viene creata solo l'associazione anagrafica (product_customers); le righe di stock non vengono modificate."
        />

        <Space style={{ marginBottom: 16 }}>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Aggiorna
          </Button>
          <Popconfirm
            title={`Creare ${pairs.length} associazioni prodotto-cliente?`}
            onConfirm={handleAssociate}
            okText="Associa"
            cancelText="Annulla"
            disabled={pairs.length === 0}
          >
            <Button type="primary" icon={<UserAddOutlined />} loading={saving} disabled={pairs.length === 0}>
              Associa prodotti selezionati ({pairs.length})
            </Button>
          </Popconfirm>
          <Text type="secondary">{items.length} prodotti senza cliente trovati</Text>
        </Space>

        <Table
          rowKey="product_id"
          columns={columns}
          dataSource={items}
          loading={loading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true }}
          locale={{ emptyText: 'Nessun prodotto senza cliente: i dati sono coerenti' }}
        />
      </Card>
    </div>
  );
};

export default StockWithoutCustomer;
